

import { useRouter } from "@/lib/navigation";
import { ArrowLeft, Plus, TrendingUp, Wallet, CreditCard, AlertCircle, Wrench, Building2 } from "lucide-react";
import Badge from "../../components/Badge";
import { EventDetail } from "./types";

interface FinancialSummary {
  total: number;
  paid: number;
  balance: number;
  profit: number;
}

interface EventHeaderProps {
  event: EventDetail;
  financials?: FinancialSummary;
  onAddService?: () => void;
  onAddVendor?: () => void;
}

function fmt(n: number) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

export default function EventHeader({ event, financials, onAddService, onAddVendor }: EventHeaderProps) {
  const router = useRouter();

  const hasFinancials = financials && financials.total > 0;
  const isPaid = hasFinancials && financials.balance <= 0;
  const paidPct = hasFinancials ? Math.min(100, Math.round((financials.paid / financials.total) * 100)) : 0;

  const eventDate = event.start_date
    ? new Date(event.start_date.split("T")[0]).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-4 py-3 bg-gradient-to-r from-stone-900 to-stone-700">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => router.push("/events")}
            className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <h1 className="font-extrabold text-white text-base sm:text-lg leading-tight truncate">
              {event.name}
            </h1>
            <div className="flex items-center gap-2 text-xs text-stone-300 mt-0.5 flex-wrap">
              <span>#{event.id}</span>
              {event.event_type && (
                <>
                  <span className="text-stone-500">·</span>
                  <span className="capitalize">{event.event_type}</span>
                </>
              )}
              {eventDate && (
                <>
                  <span className="text-stone-500">·</span>
                  <span>{eventDate}</span>
                </>
              )}
              {event.venue && (
                <>
                  <span className="text-stone-500">·</span>
                  <span>{event.venue.name}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge label={event.status} status={event.status} />
        </div>
      </div>

      {/* Financial summary bar */}
      {hasFinancials && (
        <div className="px-4 py-3 bg-stone-50 border-b border-stone-100">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-3.5 h-3.5 text-stone-500" />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">Event Total</p>
                <p className="text-sm font-bold text-stone-800">{fmt(financials.total)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">Received</p>
                <p className="text-sm font-bold text-emerald-700">{fmt(financials.paid)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isPaid ? "bg-emerald-50" : "bg-amber-50"}`}>
                <AlertCircle className={`w-3.5 h-3.5 ${isPaid ? "text-emerald-600" : "text-amber-600"}`} />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">Balance</p>
                <p className={`text-sm font-bold ${isPaid ? "text-emerald-700" : "text-amber-700"}`}>
                  {isPaid ? "Cleared" : fmt(financials.balance)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${financials.profit >= 0 ? "bg-blue-50" : "bg-rose-50"}`}>
                <TrendingUp className={`w-3.5 h-3.5 ${financials.profit >= 0 ? "text-blue-600" : "text-rose-600"}`} />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">Profit</p>
                <p className={`text-sm font-bold ${financials.profit >= 0 ? "text-blue-700" : "text-rose-700"}`}>
                  {fmt(financials.profit)}
                </p>
              </div>
            </div>
          </div>

          {/* Payment progress bar */}
          {financials.total > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-stone-400 font-medium">Payment progress</span>
                <span className="text-[10px] font-bold text-stone-600">{paidPct}%</span>
              </div>
              <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isPaid ? "bg-emerald-500" : "bg-amber-500"}`}
                  style={{ width: `${paidPct}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick action strip */}
      {(onAddService || onAddVendor) && (
        <div className="px-4 py-2 flex items-center gap-2 border-t border-stone-100 bg-white">
          <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mr-1">Add</span>
          {onAddService && (
            <button
              onClick={onAddService}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 transition-colors"
            >
              <Wrench className="w-3 h-3" />
              Service
            </button>
          )}
          {onAddVendor && (
            <button
              onClick={onAddVendor}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
            >
              <Building2 className="w-3 h-3" />
              Vendor
            </button>
          )}
        </div>
      )}
    </div>
  );
}
